import { Avatar, TagColor } from '@factorialco/deprecated-design-system'
import { F0AvatarTeam, StatusVariant } from '@factorialco/f0-react'
import {
  BankAccountForSelectFragment,
  BankingAccountDetailedFragment,
  BankingBankAccountFragment,
  BankingBankAccountOverviewFragment,
  BankingValueObjectsOnboardingCreationSourceValueEnum,
  BankingValueObjectsOnboardingStatusEnum,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

export const getDefaultAlias = (
  creationSource?: BankingValueObjectsOnboardingCreationSourceValueEnum,
  alias?: string
) => {
  if (alias) return alias
  if (!creationSource) return i18n.t('banking.accounts.type.payments_account')
  switch (creationSource) {
    case 'expenses':
    case 'expensescombined':
      return i18n.t('banking.accounts.type.expenses_account')
    case 'software':
      return i18n.t('banking.accounts.type.software_account')
    case 'payments':
      return i18n.t('banking.accounts.type.payments_account')
    case 'finance':
      return i18n.t('banking.accounts.type.finance_account')
    case 'accounts_payable':
      return i18n.t('banking.accounts.type.accounts_payable_account')
    default:
      return i18n.t('banking.accounts.type.finance_account')
  }
}

/**
 * Maps a banking onboarding status to a corresponding tag color.
 * @param status The banking onboarding status to map.
 * @returns The corresponding tag color.
 */
export const mapStatusToClass: (
  status?: BankingValueObjectsOnboardingStatusEnum | null
) => TagColor = (status) => {
  switch (status) {
    case 'pending':
    case 'missing_information':
    case 'verification_pending':
      return TagColor.champagne
    case 'in_review':
      return TagColor.malibu
    case 'rejected':
      return TagColor.radical
    case 'completed':
      return TagColor.correct
    default:
      return TagColor.viridian
  }
}

/**
 * Maps a banking onboarding status to its corresponding text.
 * @param status The banking onboarding status to map.
 * @returns The corresponding text for the given status.
 */
export const mapStatusToText: (
  status?: BankingValueObjectsOnboardingStatusEnum | null
) => string = (status) => {
  switch (status) {
    case 'pending':
    case 'missing_information':
    case 'verification_pending':
      return i18n.t('banking.accounts.status.missing_information')
    case 'in_review':
      return i18n.t('banking.accounts.status.in_review')
    case 'rejected':
      return i18n.t('banking.accounts.status.ko')
    case 'completed':
      return ''
    default:
      return ''
  }
}

export const messageFromStatus = (
  status: BankingValueObjectsOnboardingStatusEnum | undefined
): string => {
  switch (status) {
    case 'pending':
    case 'missing_information':
    case 'verification_pending':
      return i18n.t('banking.accounts.status_message.needs_completion')
    default:
      return ''
  }
}

export const getContinueOnboardingUrl = (
  onboarding: {
    legalEntityId?: number
    onboardingUrl?: string | null
    status: BankingValueObjectsOnboardingStatusEnum
  },
  authorizations: { authorizationUrl?: string | null | undefined; expired: boolean }[]
): string | null | undefined => {
  switch (onboarding.status) {
    case 'completed':
    case 'rejected':
    case 'in_review':
      return null
    case 'missing_information':
    case 'pending':
      return onboarding.onboardingUrl
    case 'verification_pending':
      // Find a non-expired authorization, or fallback to any available one
      let authorization = authorizations?.find((el) => !el.expired)
      if (!authorization && authorizations.length) {
        authorization = authorizations[0]
      }
      // Provider-agnostic: use authorization URL if available, otherwise onboarding URL
      return authorization?.authorizationUrl || onboarding.onboardingUrl
    default:
      return onboarding.onboardingUrl
  }
}

export const formattedBankAccountNumber = (
  bankAccount: BankingBankAccountFragment | BankAccountForSelectFragment
) => {
  return bankAccount.accountNumberType === 'iban'
    ? (bankAccount.iban || '').split(/(.{4})/).join(' ')
    : bankAccount.accountNumber
}

export const visibleAccountNumber = (bankAccount: BankingBankAccountFragment) => {
  return bankAccount.accountNumberType === 'iban'
    ? bankAccount.iban || ''
    : bankAccount.accountNumber
}

export const visibleExtraField = (bankAccount: BankingBankAccountFragment) => {
  switch (bankAccount.accountNumberType) {
    case 'iban':
      return bankAccount.bic
    case 'sort_code_and_account_number':
      return bankAccount.sortCode
    case 'routing_number_and_account_number':
      return bankAccount.routingNumber
    case 'bank_name_and_account_number':
      return bankAccount.bankName
    case 'clabe':
    case 'other':
      return undefined
  }
}

export const bankAccountLastDigits = (
  bankAccount: BankingBankAccountFragment | BankingBankAccountOverviewFragment
) => {
  return `•••• ${bankAccount.accountNumber.substring(bankAccount.accountNumber.length - 4)}`
}
export const bankLogo = (
  {
    institution,
  }: {
    institution?: { logo?: string; name?: string } | null
    name?: string
  },
  name = ''
) => {
  if (institution?.logo) {
    return <Avatar url={institution.logo} label={institution?.name ?? ''} size="s32" />
  } else {
    return <F0AvatarTeam name={institution?.name ?? name} size="md" />
  }
}

export const bankingAccountActions = (
  account: BankingAccountDetailedFragment,
  bankAccount: BankingBankAccountFragment
) => {
  const hasToFinishSetup = !bankAccount.finishSetupDate
  const hasToReconnect = !!(
    account.accessValidUntil && new Date(account.accessValidUntil) < new Date()
  )

  return {
    hasToFinishSetup,
    hasToReconnect,
  }
}

/**
 * Maps a banking onboarding status to a F0 StatusVariant for use with OneDataCollection.
 * @param status The banking onboarding status to map.
 * @returns The corresponding F0 StatusVariant.
 */
export const mapStatusToF0Variant = (
  status?: BankingValueObjectsOnboardingStatusEnum | null
): StatusVariant => {
  switch (status) {
    case 'pending':
    case 'missing_information':
    case 'verification_pending':
      return 'warning'
    case 'in_review':
      return 'info'
    case 'rejected':
      return 'critical'
    case 'completed':
      return 'positive'
    default:
      return 'neutral'
  }
}
